/**
 * WHO IS HOLDING THE BALL — derived, never stored. (§4.5 keeper, Law 15 throw-in)
 *
 * The render layer needs one question answered every frame: *is this ball in
 * somebody's hands right now, and whose?* This is the only place that answers
 * it, so `main.ts`, `practice.ts`, `soloPractice.ts` and `testArena.ts` cannot
 * each grow their own slightly different version.
 *
 * ── Everything here is DERIVED from fields that ALREADY clone, hash and
 *    serialise ──────────────────────────────────────────────────────────────
 *
 * `ball.possessedBy`, `players.anim`, `players.gkHoldStartTick`, `match.phase`,
 * `match.restartStage`, `match.restartTaker`. No new state, nothing written
 * back, no allocation. It is the same discipline `sim/restart-anim.ts` states
 * for the throw pose: a derived answer survives a reconciliation rewind for
 * free, and a stored one has to be cloned, hashed and probably put on the wire
 * before it is even correct once.
 *
 * ── This is presentation-only, and it is allowed to be wrong ───────────────
 *
 * Nothing in `/sim` reads this, and nothing may. The renderer's attachment is
 * bounded (`HOLD_MAX_CORRECTION_M` in `render/ball.ts`): if this function says
 * "held" and the hands are nowhere near the simulated ball, the attachment is
 * simply refused and the ball is drawn exactly where the sim put it. That bound
 * is what lets this be a cheap derivation instead of a new authoritative flag —
 * a false positive costs nothing, and a false negative costs one keeper looking
 * the way he looked before this file existed.
 */

import {
  AnimState,
  MatchPhase,
  MAX_PLAYERS,
  RestartStage,
  type WorldState,
} from '@fg/shared/sim';
import { BallHoldPose } from '../render/handAttach.js';

export interface BallHold {
  /** Slot holding the ball, or -1. */
  slot: number;
  pose: BallHoldPose;
}

/** Reused across frames — this runs every frame in four loops (§15.7). */
const scratch: BallHold = { slot: -1, pose: BallHoldPose.None };

/**
 * The hold for this tick, or `{ slot: -1, pose: None }`.
 *
 * ⚠ The returned object is SHARED SCRATCH. Read it, do not keep it.
 */
export function resolveBallHold(world: WorldState): Readonly<BallHold> {
  scratch.slot = -1;
  scratch.pose = BallHoldPose.None;

  const gk = keeperHolding(world);
  if (gk >= 0) {
    scratch.slot = gk;
    // A gathered ball is held in BOTH gloves and belongs between them. Every
    // one-handed keeper state (parry, punch) has already released the ball in
    // the sim — `releaseBall` clears `gkHoldStartTick` and `possessedBy` on the
    // same tick it stamps GkThrow/GkRollOut/GkKick — so a keeper who is still
    // holding is, without exception, holding it two-handed.
    scratch.pose = BallHoldPose.BothHands;
    return scratch;
  }

  const t = throwInTaker(world);
  if (t >= 0) {
    scratch.slot = t;
    scratch.pose = BallHoldPose.BothHands; // Law 15: from behind and over the head
  }
  return scratch;
}

/**
 * The keeper with the ball in his gloves — gather, hold, carry, smother.
 *
 * TWO SIGNALS, because only one of them survives the wire. `gkHoldStartTick` is
 * the authoritative flag (`stepHeldBall` re-asserts the hold off it every
 * tick), but `net/snapshot.ts` deliberately omits it from the snapshot as
 * "state a remote client cannot act on". `ball.possessedBy` and `players.anim`
 * ARE both serialised, and `stepHeldBall` stamps `GkCatch` on every tick of the
 * hold — so an online spectator's keeper is recognised by those instead. The
 * local, predicted world satisfies the first test and never reaches the second.
 */
function keeperHolding(world: WorldState): number {
  const id = world.ball.possessedBy;
  if (id < 0 || id >= MAX_PLAYERS) return -1;
  const p = world.players;
  if (p.active[id] !== 1) return -1;
  if (p.gkHoldStartTick[id]! >= 0) return id;
  const a = p.anim[id]!;
  return a === AnimState.GkCatch || a === AnimState.GkSmother ? id : -1;
}

/**
 * The throw-in taker with the ball above his head.
 *
 * `rules/fsm.ts` already puts the SIMULATED ball between his hands —
 * `attachThrowInBallToHands` runs every Ready tick and through the authored
 * release wind-up, at `THROW_RELEASE_HEIGHT` behind his head. So the render
 * attachment here is a small correction (upright-frame estimate → the actual
 * bones), not a relocation.
 *
 * Setup is included even though the ball is still dead on the touchline during
 * it: the taker is walking to fetch it and `stepRestartAnim` already gives him
 * `ThrowInHold` on arrival. The renderer's distance bound is what decides
 * whether the ball has actually reached his hands yet, which is a better test
 * than a stage number — it stays right if `feat/restart-polish` changes when
 * the pickup happens.
 */
function throwInTaker(world: WorldState): number {
  const m = world.match;
  if (m.phase !== MatchPhase.ThrowIn) return -1;
  if (m.restartStage !== RestartStage.Setup && m.restartStage !== RestartStage.Ready) return -1;
  const t = m.restartTaker;
  if (t < 0 || t >= MAX_PLAYERS) return -1;
  const p = world.players;
  if (p.active[t] !== 1 || p.sentOff[t] === 1) return -1;
  const a = p.anim[t]!;
  return a === AnimState.ThrowInHold || a === AnimState.Throw ? t : -1;
}
